import { useEffect, useState } from "react";
import { Save, X } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import ImageManager from "@/components/admin/ImageManager";

const CATEGORIES = ["Tops", "Bodysuits", "Sports Bras", "Biker Shorts", "Joggers", "Accessories", "Contraband"];
const TIERS = ["mainline", "contraband"];
const COLLECTIONS = ["drop-001", "contraband"];

const CLOTHING_SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL"];
const SHOE_SIZE_ORDER = [
  "5.5", "6", "6.5", "7", "7.5", "8", "8.5",
  "9", "9.5", "10", "10.5", "11", "11.5",
];
const SIZE_TYPE_OPTIONS = [
  { value: "clothing", label: "Clothing (XXS–XL)" },
  { value: "shoe", label: "Shoe (5.5–11.5)" },
];
const SIZE_STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "preorder", label: "Preorder" },
  { value: "sold_out", label: "Sold Out" },
  { value: "hidden", label: "Hidden" },
];

function sizeOrderFor(sizeType) {
  return sizeType === "shoe" ? SHOE_SIZE_ORDER : CLOTHING_SIZE_ORDER;
}

function defaultSizeMatrix(slug = "", sizeType = "clothing") {
  if (sizeType === "shoe") {
    return SHOE_SIZE_ORDER.map((s) => ({
      size: s,
      enabled: true,
      status: "preorder",
      quantity: 0,
      sku_suffix: "",
    }));
  }
  return CLOTHING_SIZE_ORDER.map((s) => {
    const enabled = s !== "XXS" || slug === "meet-me-in-the-void-cropped-long-sleeve";
    return {
      size: s,
      enabled,
      status: enabled ? "preorder" : "hidden",
      quantity: 0,
      sku_suffix: "",
    };
  });
}

function mergeSizeMatrix(incoming, slug = "", sizeType = "clothing") {
  const base = defaultSizeMatrix(slug, sizeType);
  if (!Array.isArray(incoming) || incoming.length === 0) return base;
  const map = new Map(incoming.map((s) => [s.size, s]));
  return base.map((b) => {
    const found = map.get(b.size);
    if (!found) return b;
    return {
      size: b.size,
      enabled: Boolean(found.enabled),
      status: found.status || (found.enabled ? "preorder" : "hidden"),
      quantity: Number.isFinite(Number(found.quantity)) ? Number(found.quantity) : 0,
      sku_suffix: found.sku_suffix ?? "",
    };
  });
}

const DEFAULTS = {
  slug: "",
  name: "",
  sku: "",
  category: "Tops",
  subcategory: "",
  collection: "drop-001",
  tier: "mainline",
  status: "Sample Approved",
  status_badge: "DROP 001 PRE-DROP",
  price: "",
  short_desc: "",
  long_desc: "",
  fit_notes: "",
  fabric_notes: "",
  style_notes: "",
  shipping_notes: "",
  placeholder_label: "GARMENT FILE",
  images: [],
  size_type: "clothing",
  sizes: defaultSizeMatrix("", "clothing"),
  published: true,
};

function slugify(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

export default function ProductForm({ existing, onSaved, onCancel }) {
  const isEdit = Boolean(existing);
  const [form, setForm] = useState(() => ({ ...DEFAULTS, ...(existing || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const base = { ...DEFAULTS, ...(existing || {}) };
    base.size_type = (existing?.size_type === "shoe") ? "shoe" : "clothing";
    base.sizes = mergeSizeMatrix(existing?.sizes, existing?.slug, base.size_type);
    setForm(base);
    setError("");
  }, [existing]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function changeSizeType(nextType) {
    setForm((f) => ({
      ...f,
      size_type: nextType,
      sizes: mergeSizeMatrix(f.sizes, f.slug, nextType),
    }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form };
      // Normalize price
      if (payload.price === "" || payload.price === null) {
        payload.price = null;
      } else {
        payload.price = Number(payload.price);
        if (Number.isNaN(payload.price)) {
          throw new Error("Price must be a number.");
        }
      }
      // Clean optional string fields — keep them; backend accepts empty strings
      if (isEdit) {
        // Slug locked; do not send
        const { slug, ...updateBody } = payload;
        const { data } = await api.patch(`/admin/products/${existing.slug}`, updateBody);
        onSaved(data, "updated");
      } else {
        if (!payload.slug) payload.slug = slugify(payload.name);
        const { data } = await api.post("/admin/products", payload);
        onSaved(data, "created");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="admin-product-form"
      className="border border-[#2a1a3a] bg-[#06000e]/70 p-5 md:p-7 flex flex-col gap-5"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
            // {isEdit ? "Edit Product" : "Create Product"}
          </div>
          <h3 className="mt-1 font-display text-xl md:text-2xl tracking-tight">
            {form.name || (isEdit ? existing.slug : "New product")}
          </h3>
        </div>
        <button
          type="button"
          onClick={onCancel}
          data-testid="admin-product-form-cancel"
          className="btn-ghost"
          aria-label="Cancel"
        >
          <X size={12} strokeWidth={1.5} /> Cancel
        </button>
      </div>

      {/* Visibility */}
      <div className="border border-[#2a1a3a] bg-[#0a0316]/50 p-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">// Visibility</div>
          <p className="mt-1 text-[13px] text-[#b4a3c7] max-w-md leading-snug">
            Hidden products do not appear on Shop, Drop 001, related rails, or product URLs (404 publicly). Still visible here in admin.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="pf-published"
            checked={form.published !== false}
            onChange={(e) => update("published", e.target.checked)}
            className="accent-[#ff1493] w-4 h-4"
          />
          <span className={`font-mono text-[11px] tracking-[0.22em] uppercase ${form.published !== false ? "text-[#00ff41]" : "text-[#7a6a90]"}`}>
            {form.published !== false ? "Published" : "Hidden"}
          </span>
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Name" required value={form.name} onChange={(v) => update("name", v)} testId="pf-name" />
        <Field
          label="Slug (URL)"
          required={!isEdit}
          disabled={isEdit}
          value={form.slug}
          onChange={(v) => update("slug", slugify(v))}
          hint={isEdit ? "Slug is locked after creation." : "Auto-generated from name if blank."}
          testId="pf-slug"
        />
        <Field label="SKU" required value={form.sku} onChange={(v) => update("sku", v)} testId="pf-sku" />
        <Field
          label="Price (USD)"
          type="number"
          step="0.01"
          value={form.price ?? ""}
          onChange={(v) => update("price", v)}
          hint="Leave blank for 'price at drop'."
          testId="pf-price"
        />
        <Select
          label="Category"
          required
          value={form.category}
          options={CATEGORIES}
          onChange={(v) => update("category", v)}
          testId="pf-category"
        />
        <Field
          label="Subcategory / Filter"
          value={form.subcategory || ""}
          onChange={(v) => update("subcategory", v)}
          hint="Optional. Shows as Shop filter pill."
          testId="pf-subcategory"
        />
        <Select
          label="Tier"
          value={form.tier}
          options={TIERS}
          onChange={(v) => update("tier", v)}
          testId="pf-tier"
        />
        <Select
          label="Collection"
          value={form.collection}
          options={COLLECTIONS}
          onChange={(v) => update("collection", v)}
          testId="pf-collection"
        />
        <Field
          label="Status"
          value={form.status || ""}
          onChange={(v) => update("status", v)}
          testId="pf-status"
        />
        <Field
          label="Status Badge"
          value={form.status_badge || ""}
          onChange={(v) => update("status_badge", v)}
          testId="pf-status-badge"
        />
        <Field
          label="Placeholder Label"
          value={form.placeholder_label || ""}
          onChange={(v) => update("placeholder_label", v)}
          hint="Used when no images are uploaded."
          testId="pf-placeholder"
        />
      </div>

      <Textarea label="Short Description" value={form.short_desc} onChange={(v) => update("short_desc", v)} testId="pf-short-desc" />
      <Textarea label="Long Description" rows={4} value={form.long_desc} onChange={(v) => update("long_desc", v)} testId="pf-long-desc" />
      <Textarea label="Fit Notes" rows={3} value={form.fit_notes} onChange={(v) => update("fit_notes", v)} testId="pf-fit" />
      <Textarea label="Fabric Notes" rows={3} value={form.fabric_notes} onChange={(v) => update("fabric_notes", v)} testId="pf-fabric" />
      <Textarea label="Style Notes" rows={2} value={form.style_notes} onChange={(v) => update("style_notes", v)} testId="pf-style" />
      <Textarea label="Shipping / Preorder" rows={2} value={form.shipping_notes} onChange={(v) => update("shipping_notes", v)} testId="pf-shipping" />

      <div className="border-t border-[#2a1a3a] pt-5">
        <SizeMatrixEditor
          sizes={form.sizes}
          sizeType={form.size_type}
          onChangeSizeType={changeSizeType}
          onChange={(rows) => update("sizes", rows)}
        />
      </div>

      <div className="border-t border-[#2a1a3a] pt-5">
        <ImageManager images={form.images || []} onChange={(imgs) => update("images", imgs)} />
      </div>

      {error && (
        <p data-testid="admin-product-form-error" className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#ff3b30]">
          // {error}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-outline">
          Cancel
        </button>
        <button
          type="submit"
          data-testid="admin-product-form-save"
          disabled={saving}
          className="btn-primary"
        >
          <Save size={13} strokeWidth={1.5} />
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, required, disabled, hint, type = "text", step, testId }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">
        // {label}{required && " *"}
      </span>
      <input
        data-testid={testId}
        type={type}
        step={step}
        required={required}
        disabled={disabled}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`field ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      />
      {hint && (
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#7a6a90]">{hint}</span>
      )}
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 2, testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">// {label}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="field resize-y"
      />
    </label>
  );
}

function Select({ label, value, options, onChange, required, testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493]">
        // {label}{required && " *"}
      </span>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="field"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-black">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function SizeMatrixEditor({ sizes, sizeType = "clothing", onChange, onChangeSizeType }) {
  const order = sizeOrderFor(sizeType);
  const rows = Array.isArray(sizes) && sizes.length === order.length ? sizes : mergeSizeMatrix(sizes, "", sizeType);

  function updateRow(idx, patch) {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  }

  return (
    <div data-testid="size-matrix-editor">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
          // Sizes / Inventory
        </div>
        <label className="inline-flex items-center gap-2">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#7a6a90]">
            // Size system
          </span>
          <select
            data-testid="size-type-select"
            value={sizeType}
            onChange={(e) => onChangeSizeType?.(e.target.value)}
            className="field !py-1.5"
          >
            {SIZE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-black">
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-1 text-[13px] text-[#b4a3c7] max-w-2xl leading-snug">
        Toggle each size on or off, set status, and track preorder quantity. Hidden sizes will not
        appear on the public product page. Sold-out sizes appear disabled. Switch the size system
        to <em>Shoe</em> for shower slides and other footwear.
      </p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="size-matrix-table">
          <thead>
            <tr className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a6a90] text-left">
              <th className="py-2 pr-3 w-16">Size</th>
              <th className="py-2 pr-3 w-20">On</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 w-28">Quantity</th>
              <th className="py-2 pr-3 w-36">SKU suffix</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {rows.map((row, i) => {
              const isHidden = !row.enabled || row.status === "hidden";
              return (
                <tr key={row.size} data-testid={`size-row-${row.size}`}>
                  <td className="py-2 pr-3 font-display text-base text-white">{row.size}</td>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      data-testid={`size-enabled-${row.size}`}
                      checked={Boolean(row.enabled)}
                      onChange={(e) =>
                        updateRow(i, {
                          enabled: e.target.checked,
                          status: e.target.checked && row.status === "hidden" ? "preorder" : row.status,
                        })
                      }
                      className="accent-[#ff1493] w-4 h-4"
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      data-testid={`size-status-${row.size}`}
                      value={row.status}
                      onChange={(e) => updateRow(i, { status: e.target.value })}
                      disabled={!row.enabled}
                      className={`field !py-1.5 ${!row.enabled ? "opacity-50" : ""}`}
                    >
                      {SIZE_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className="bg-black">
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min={0}
                      data-testid={`size-qty-${row.size}`}
                      value={row.quantity ?? 0}
                      onChange={(e) =>
                        updateRow(i, { quantity: Math.max(0, parseInt(e.target.value || "0", 10) || 0) })
                      }
                      disabled={!row.enabled}
                      className={`field !py-1.5 ${!row.enabled ? "opacity-50" : ""}`}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="text"
                      maxLength={16}
                      placeholder="optional"
                      data-testid={`size-sku-${row.size}`}
                      value={row.sku_suffix ?? ""}
                      onChange={(e) => updateRow(i, { sku_suffix: e.target.value })}
                      disabled={!row.enabled}
                      className={`field !py-1.5 ${!row.enabled ? "opacity-50" : ""} ${isHidden ? "text-[#7a6a90]" : ""}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

